import {
  LoadingSkeleton,
  Menu,
} from "@twinkltech/curriculum-architect-component-library";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { IconNavigationHome } from "@twinkltech/web-ui";
import { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { SIDEBAR_NAV_NS } from "./SidebarNav.i18n";

type SidebarNavProps = {
  canViewCurricula?: boolean;
  isLoading?: boolean;
} & Omit<ComponentProps<typeof Menu>, "aria-label" | "ariaCloseLabel">;

const MenuSkeleton = (props: SidebarNavProps) => {
  return (
    <Menu hasCollapseButton aria-label={""} variant="dark" {...props}>
      <div className="flex flex-col gap-12 px-8 py-6">
        <div className="flex flex-col gap-4">
          <LoadingSkeleton height="1rem" width="45%" />
          <LoadingSkeleton height="1rem" width="100%" />
          <LoadingSkeleton height="1rem" width="100%" />
        </div>
      </div>
      <div className="flex flex-col gap-8 px-8 py-6">
        <div className="flex flex-col gap-4">
          <LoadingSkeleton height="1rem" width="45%" />
          <LoadingSkeleton height="1rem" width="100%" />
          <LoadingSkeleton height="1rem" width="100%" />
        </div>
      </div>
      <div className="flex flex-col gap-8 px-8 py-6">
        <div className="flex flex-col gap-4">
          <LoadingSkeleton height="1rem" width="45%" />
          <LoadingSkeleton height="1rem" width="100%" />
          <LoadingSkeleton height="1rem" width="100%" />
        </div>
      </div>
      <div className="flex flex-col gap-8 px-8 py-6">
        <div className="flex flex-col gap-4">
          <LoadingSkeleton height="1rem" width="45%" />
        </div>
      </div>
    </Menu>
  );
};
export const SidebarNav = ({
  canViewCurricula,
  isLoading,
  ...props
}: SidebarNavProps) => {
  const { t } = useTranslation(SIDEBAR_NAV_NS);
  const { click } = useTwinklAnalytics();

  const { feedbackState, onFeedbackEngaged } = useFeedback();

  if (isLoading) {
    return <MenuSkeleton {...props} />;
  }

  return (
    <Menu
      hasCollapseButton
      aria-label={t("a11y.nav")}
      ariaCloseLabel={t("a11y.close")}
      variant="dark"
      {...props}
    >
      {canViewCurricula && (
        <Menu.Group label={t("headings.design-teach")}>
          <Menu.Item>
            <Menu.Link as={NavLink} to={paths.CURRICULUM_LIST_PATH}>
              <div className="flex items-center gap-4">
                <IconNavigationHome className="min-w-[24px] mr-[1px]" />
                {t("terms.my-curricula", { ns: COMMON_NS })}
              </div>
            </Menu.Link>
          </Menu.Item>
        </Menu.Group>
      )}
      <Menu.Group label={t("headings.explore")}>
        <Menu.Item>
          <Menu.LinkWithIcon
            as={NavLink}
            icon="Dictionary"
            to={paths.EXPLORE_SCHEME_LIST_CONTENTS_PATH}
            onClick={() =>
              click("click_side_bar_menu", {
                menuName: "Browse Units",
              })
            }
          >
            {t("items.explore-subject-contents")}
          </Menu.LinkWithIcon>
        </Menu.Item>
        {import.meta.env.VITE_ENABLE_DESIGN_PRINCIPLES === "true" && (
          <Menu.Item>
            <Menu.LinkWithIcon
              as={NavLink}
              icon="FactCheck"
              to={paths.CURRICULUM_GOAL_PATH}
              onClick={() =>
                click("click_side_bar_menu", {
                  menuName: "Curriculum Goals",
                })
              }
            >
              {t("items.curriculum-goals")}
            </Menu.LinkWithIcon>
          </Menu.Item>
        )}
      </Menu.Group>
      <Menu.Group label={t("headings.support")}>
        <Menu.Item>
          <Menu.LinkWithIcon
            as="a"
            href={paths.external.CUSTOMER_SUPPORT_URL}
            icon="SupportAgent"
            rel="noopener noreferrer"
            target="_blank"
            onClick={() =>
              click("click_side_bar_menu", {
                menuName: "Customer Support",
              })
            }
          >
            {t("items.customerSupport")}
          </Menu.LinkWithIcon>
        </Menu.Item>
        <Menu.Item>
          <Menu.LinkWithIcon
            as="a"
            href={paths.external.FAQ_URL}
            icon="ContactSupport"
            rel="noopener noreferrer"
            target="_blank"
            onClick={() =>
              click("click_side_bar_menu", {
                menuName: "FAQ",
              })
            }
          >
            {t("items.faq")}
          </Menu.LinkWithIcon>
        </Menu.Item>
      </Menu.Group>
      <Menu.Group label={t("headings.more")}>
        <Menu.Item>
          <span className="relative">
            <Menu.LinkWithIcon
              as="a"
              href={paths.external.FEEDBACK_SURVEY_URL}
              icon="Comment"
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                onFeedbackEngaged();
                click("click_side_bar_menu", {
                  menuName: "Leave Feedback",
                });
              }}
            >
              <span className="relative">
                {t("items.leaveFeedback")}
                {feedbackState === "pip" && (
                  <span className="absolute -top-1 -left-6">
                    <span className="relative flex h-3 w-3">
                      <span
                        className="absolute inline-flex h-full w-full rounded-full bg-pink-400"
                        style={{
                          animation:
                            "feedbackPing 1s cubic-bezier(0, 0, 0.2, 1) 2 forwards",
                          animationName: "feedbackPing",
                        }}
                      ></span>
                      <style>{`
                        @keyframes feedbackPing {
                          0% {
                            transform: scale(1);
                            opacity: 1;
                          }
                          75%, 100% {
                            transform: scale(2);
                            opacity: 0;
                          }
                        }
                      `}</style>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-pink-500 border-2 border-neutral-100" />
                    </span>
                  </span>
                )}
              </span>
            </Menu.LinkWithIcon>
          </span>
        </Menu.Item>
      </Menu.Group>
    </Menu>
  );
};
